<template>
  <el-dialog
    v-model="dialogVisible"
    title="图片裁剪"
    width="50%"
    destroy-on-close
    @close="handleDialogClose"
  >
    <div class="crop-wrapper">
      <!-- 左侧比例选择 -->
      <div class="Ratio">
        <div class="RatioList">
          <div
            v-for="item in ratioList"
            :key="item.value"
            :class="['item', { active: currentRatio == item.label || currentRatio == item.value }]"
            @click="handleRatioChange(item.value, item.label)"
          >
            {{ item.label }}
          </div>
        </div>
        <el-button
          class="btn"
          type="primary"
          @click="handleConfirm"
        >
          确认裁剪
        </el-button>
      </div>
      <!-- 中间裁剪区域 -->
      <div class="crop-container">
        <img
          ref="imgRef"
          :src="imageSrc"
          alt="裁剪图片"
          @load="handleImgLoad"
        >
        <div class="dockMain">
          <div
            v-show="currentRatio != 'free'"
            class="dockBtn"
          >
            ratio:{{ currentRatio }}
          </div>
          <div class="dockBtn" />
          <div
            class="dockBtn"
            @click="handleRotate(-45)"
          >
            <svg-icon icon-class="antLine" />
          </div>
          <div
            class="dockBtn"
            @click="handleRotate(45)"
          >
            <svg-icon icon-class="antLine2" />
          </div>
          <div
            class="dockBtn"
            @click="handleFlip('vertical')"
          >
            <svg-icon icon-class="antLine3" />
          </div>
          <div
            class="dockBtn"
            @click="handleFlip('horizontal')"
          >
            <svg-icon icon-class="antLine4" />
          </div>
          <div
            class="dockBtn"
            @click="handleReset()"
          >
            <svg-icon icon-class="info" />
          </div>
        </div>
      </div>
    </div>

    <!-- <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确认裁剪</el-button>
    </template> -->
  </el-dialog>
</template>

<script setup lang="ts">
import { getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { addMaterialApi } from '../../api/brand/material.ts';
const { proxy } = getCurrentInstance()!;

// ✅ 严格类型定义（解决TS报错核心）
interface Props {
  visible: boolean; // 弹窗显隐
  imageSrc: string; // 原始图片地址
  categoryId: string;
}

interface RatioItem {
  label: string;
  value: string | number; // free=自由裁剪，数字=比例
}

// ✅ 正确使用defineProps（修复withDefineProps报错）
const props = defineProps<Props>();
const emit = defineEmits<{
  close: []; // 弹窗关闭
  confirm: [file: File]; // 裁剪完成返回File
}>();

// 响应式数据
const dialogVisible = ref(props.visible);
const imgRef = ref<HTMLImageElement | null>(null);
const cropper = ref<Cropper | null>(null);
const scaleValue = ref(1);

const currentRatio = ref<string | number>('free');
const isImgLoaded = ref(false); // 标记图片是否加载完成

// 裁剪比例列表
const ratioList: RatioItem[] = [
  { label: proxy.$t('material.Free'), value: 'free' },
  { label: '1:1', value: 1 / 1 },
  { label: '2:3', value: 2 / 3 },
  { label: '3:2', value: 3 / 2 },
  { label: '3:4', value: 3 / 4 },
  { label: '4:3', value: 4 / 3 },
  { label: '9:16', value: 9 / 16 },
  { label: '16:9', value: 16 / 9 },
];

// ✅ 监听弹窗显隐（同步props）
watch(
  () => props.visible,
  (val) => {
    dialogVisible.value = val;
    // 弹窗打开时重置状态
    if (val) {
      scaleValue.value = 1;
      currentRatio.value = 'free';
    }
  },
  { immediate: true },
);

// ✅ 图片加载完成后初始化Cropper（解决Cropper=null报错）
const handleImgLoad = () => {
  if (!imgRef.value) return;
  isImgLoaded.value = true;

  // 销毁旧实例（避免重复创建）
  if (cropper.value) {
    cropper.value.destroy();
  }

  // 初始化裁剪器
  cropper.value = new Cropper(imgRef.value, {
    aspectRatio: NaN, // 初始自由裁剪
    viewMode: 1,
    cropBoxResizable: true,
    autoCropArea: 0.5, // 图片范围
    //   cropBoxResizable: true,
    cropBoxMovable: true,
    // 4. 其他基础配置（按需保留）
    dragMode: 'move',
    zoom: (val) => {
      //
      console.log('ref---------', val);
    },
  });
};

// 比例切换
const handleRatioChange = (val: string | number, name: string) => {
  if (!cropper.value || !isImgLoaded.value) return;
  // ✅ 类型守卫（解决TS类型报错）
  const aspectRatio = val === 'free' ? NaN : (val as number);
  currentRatio.value = val === 'free' ? val : name;
  cropper.value.setAspectRatio(aspectRatio);
};

// 旋转
const handleRotate = (deg: number) => {
  if (!cropper.value) return;
  cropper.value.rotate(deg);
};

// 翻转
const handleFlip = (direction: 'horizontal' | 'vertical') => {
  if (!cropper.value) return;
  if (direction === 'horizontal') {
    const currentScaleX = cropper.value.getData().scaleX || 1;
    cropper.value.scaleX(-currentScaleX);
  } else {
    const currentScaleY = cropper.value.getData().scaleY || 1;
    cropper.value.scaleY(-currentScaleY);
  }
};

// 重置
const handleReset = () => {
  if (!cropper.value) return;
  cropper.value.reset();
  scaleValue.value = 1;
  currentRatio.value = 'free';
};

// 确认裁剪（返回File）
const handleConfirm = () => {
  if (!cropper.value) return;

  const maxSize = 1 * 1024 * 1024; // 1MB
  const canvas = cropper.value.getCroppedCanvas();

  // 递归压缩图片直到小于1MB
  const compressImage = (quality: number) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        // 如果压缩后仍大于1MB且质量还能降低，则继续压缩
        if (blob.size > maxSize && quality > 0.1) {
          compressImage(quality - 0.1);
          return;
        }

        const file = new File([blob], `cropped_${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        console.log('compressed file size:', (blob.size / 1024).toFixed(2), 'KB');

        addMaterialApi({
          file,
          type: 'IMAGE',
          categoryId: props.categoryId,
        }).then((res) => {
          if (res.code == 200) {
            dialogVisible.value = false;
            emit('confirm');
          }
        });
      },
      'image/jpeg',
      quality, // 图片质量 0-1
    );
  };

  // 从高质量开始压缩
  compressImage(0.9);
};

// 弹窗关闭
const handleDialogClose = () => {
  emit('close');
};

// 组件销毁前清理Cropper（避免内存泄漏）
onBeforeUnmount(() => {
  if (cropper.value) {
    cropper.value.destroy();
    cropper.value = null;
  }
});
</script>

<style scoped lang="scss">
.Ratio {
  width: 230px;
  height: 500px;
  text-align: center;
  .RatioList {
    padding-top: 15px;
    width: 230px;
    border: #eee 1px solid;
    .item {
      line-height: 26px;
      margin-bottom: 10px;
      text-align: center;
      font-size: 16px;
      font-weight: 500;
      &.active {
        color: var(--color-1);
      }
      &:hover {
        color: var(--color-1);
        cursor: pointer;
      }
    }
  }
  .btn {
    width: 100px;
    margin: 20px auto;
  }
}
.dockMain {
  position: absolute;
  bottom: 5px;
  left: 5px;
  z-index: 1002;
  box-sizing: border-box;
  padding: 5px 5px;
  border-radius: 5px;
  opacity: 0.5;
  transition: opacity 0.5s;
  .dockBtn {
    display: inline-block;
    height: 24px;
    margin-right: 4px;
    padding: 2px 4px;
    color: var(--color-1);
    font-size: 10px;
    line-height: 16px;
    background-color: #ecf5ff;
    border: 1px solid #c6e2ff;
    border-radius: 3px;
    cursor: pointer;
    transition:
      background 0.2s,
      color 0.2s;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
}
.crop-wrapper {
  display: flex;
  gap: 20px;
  height: 500px;
  align-items: stretch;
}

.crop-container {
  position: relative;
  flex: 1;
  background: #f5f5f5;
  border: 1px dashed #dcdcdc;
  overflow: hidden;
  height: 100%;
}

.crop-container img {
  max-width: 80%;
  max-height: 80%;
  object-fit: contain;
}

.operate-card {
  width: 220px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.operate-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}
</style>
